import { Subject } from 'rxjs'
import { Injectable } from '@tanbo/di'
import { Http } from '@/services/http'
import { ElNotification } from 'element-plus'
import { h, nextTick, Component } from 'vue'
import FeatureRelease from '@/views/_components/message-template/feature-release/feature-release.vue'
import GenuineSoftware from '@/views/_components/message-template/genuine-software/genuine-software.vue'
import ServiceSubscribe from '@/views/_components/message-template/service-subscribe/service-subscribe.vue'
import ServiceExpired from '@/views/_components/message-template/service-expired/service-expired.vue'
import ServiceSoon from '@/views/_components/message-template/service-soon/service-soon.vue'
import ApprovalRemind from '@/views/_components/message-template/approval-remind/approval-remind.vue'
import { team, user } from '@/common/basis/root-injector'
import CustomMessage from '@/views/_components/message-template/custom-message/custom-message.vue'
import { deserialize } from '@/utils/tool'

export enum NotifyType {
  /** 自定义提醒 */
  customMessage = 1,
  /** 功能上线提醒 */
  featureRelease = 2,
  /** 正版软件授权提醒 */
  genuineSoftware = 3,
  /** 服务开通提醒 */
  serviceSubscribe = 4,
  /** 服务即将过期提醒 */
  serviceSoon = 5,
  /** 服务过期提醒 */
  serviceExpired = 6,
  /** 审批提醒 */
  approvalRemind = 7,
}

/**
 * 消息类型-模板组件 映射
 *
 * 这些组件需要通过 ElNotification 动态创建，
 * 不能用useReflectiveInjector来注入MessageService，
 * 因为ElNotification动态创建不在依赖注入上下文中，
 * 可以直接引用root-injector.ts中messageService单例。
 */
export const MessageTemplate: Record<number, Component> = {
  /** 自定义提醒 */
  [NotifyType.customMessage]: CustomMessage,
  /** 功能上线提醒 */
  [NotifyType.featureRelease]: FeatureRelease,
  /** 正版软件授权提醒 */
  [NotifyType.genuineSoftware]: GenuineSoftware,
  /** 服务开通提醒 */
  [NotifyType.serviceSubscribe]: ServiceSubscribe,
  /** 服务即将过期提醒 */
  [NotifyType.serviceSoon]: ServiceSoon,
  /** 服务过期提醒 */
  [NotifyType.serviceExpired]: ServiceExpired,
  /** 审批提醒 */
  [NotifyType.approvalRemind]: ApprovalRemind,
}

export interface PopupMessageReq {
  /** 返回条数 */
  limit?: number
}

export interface MessageData {
  /** id */
  id: string
  /** 消息类型 */
  notify_type: number
  /** 消息标题 */
  title: string
  /** 消息内容 */
  content: any
  /** 消息时间 */
  create_time: string
  /** 是否需要弹窗:1-需要弹窗 0-无需弹窗 */
  need_alert: number
  /** 是否已读 1-已读，0-未读 */
  is_read: number
}

export interface PopupMessageRes {
  data?: MessageData[]
  /** 未读数量 */
  count?: number
  /** 是否有下一页 */
  hasNext?: boolean
}

@Injectable()
export class MessageService {
  private pollInterval = 15000
  private pollTimer

  unreadCount = 0
  /** 未读消息数量变化发布者 */
  unreadCountSubject = new Subject<number>()

  /** el-notification 实例 Map */
  notificationInstanceMap = new Map<string, any>()

  constructor (private http: Http) {
    this.startPollUnread()
    document.addEventListener('visibilitychange', () => this.visibilityListener())
  }

  private visibilityListener () {
    if (document.visibilityState === 'hidden') {
      this.stopPollUnread()
    } else {
      this.startPollUnread()
    }
  }

  startPollUnread () {
    this.fetchUnread()
    this.stopPollUnread()
    this.pollTimer = setInterval(() => this.fetchUnread(), this.pollInterval)
  }

  stopPollUnread () {
    clearInterval(this.pollTimer)
  }

  clearUnreadCount () {
    setTimeout(() => {
      this.unreadCount = 0
      this.unreadCountSubject.next(0)
    }, 500)
  }

  fetchUnread () {
    // 消息包括团队内消息，所有全部在选择团队后才能请求
    if (!(user.hasUser() && team.hasTeam())) return

    this.getUnreadApi({ limit: 10 }).then(async res => {
      this.unreadCount = res.count
      this.unreadCountSubject.next(this.unreadCount)
      for (const item of res.data) {
        await nextTick() // 必须等上一个渲染完成后，才能弹出下一条，不然top会计算错误。
        this.popupMessage(item)
      }
    })
  }

  getUnreadApi (data: PopupMessageReq) {
    return this.http.post<PopupMessageRes>('/message/unread_list', data).then(res => {
      /** 调试自定义消息 */
      // res.data.unshift({
      //   "id": "184",
      //   "notify_type": 1,
      //   "title": "自定义消息通知",
      //   "content":"{\"html\":\"为优化平台功能架构、简化操作路径，原 <strong>软件资产</strong> 模块合并至 <strong>终端资产</strong> 模块。软件资产下的所有功能均保留完整，入口调整为 <span cm_custom_message_link=\\\"/dashboard/software-manage\\\">终端资产-软件管理</span> </div>\"}",
      //   "create_time": "2025-11-14 11:08:45",
      //   "need_alert": 1,
      //   "is_read": 1
      // })

      res.data.forEach(v => v.content = formatContent(v.content))
      return res
    })
  }

  generateNotification (contentComp, notificationKey) {
    return ElNotification({
      message: contentComp, // 直接传入 VNode
      customClass: 'internal-messages',
      type: 'warning',
      duration: 0,
      offset: 50,
      onClose: () => {
        this.notificationInstanceMap.delete(notificationKey)
      },
    })
  }

  popupMessage (messageData: MessageData) {
    const messageComponent = MessageTemplate[messageData.notify_type]
    if (!messageComponent) {
      console.log(`Unknown notify_type: ${messageData.notify_type}`)
      return
    }

    // 生成唯一的 notificationKey
    const notificationKey = `notification_${Math.random().toString(36).substr(2, 9)}`

    // 先创建 VNode，传入 key
    const contentComp = h(messageComponent, {
      header: messageData.title,
      content: messageData.content,
      notificationKey: notificationKey, // 传入 key，让组件可以通过 key 获取 instance
    })

    // 创建通知
    const instance = this.generateNotification(contentComp, notificationKey)

    // 将 instance 存储到 Map
    this.notificationInstanceMap.set(notificationKey, instance)
  }

  /**
   * 根据 key 获取 notification 实例
   */
  getNotificationInstance (key: string) {
    return this.notificationInstanceMap.get(key) as any || null
  }

  /**
   * 关闭所有 notification 实例
   */
  closeAllNotifications () {
    if (this.notificationInstanceMap.size === 0) return

    // 先收集所有的 instance，避免在遍历过程中 Map 被修改
    const instanceArray: any[] = []
    this.notificationInstanceMap.forEach((instance: any) => {
      instanceArray.push(instance)
    })

    // 关闭所有通知（这会触发 onClose 回调，自动清理 Map）
    instanceArray.forEach((instance: any) => {
      if (instance && typeof instance.close === 'function') {
        instance.close()
      }
    })

    // 确保清空 Map（防止某些情况下 onClose 回调没有执行）
    this.notificationInstanceMap.clear()
  }
}

export function formatContent (str: string): any {
  const content = deserialize(str) as any
  if (content.service_names === null) content.service_names = []
  return content
}
